import React, { useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Drawer, Form, InputNumber, Button, Space, Divider, Typography, Collapse, message,
  Popconfirm, Row, Col, Tabs, Alert, Tooltip, Popover
} from 'antd';
import { ReloadOutlined, SaveOutlined, LockOutlined, InfoCircleOutlined, FilePdfOutlined } from '@ant-design/icons';
import api from '../../../services/api';
import { useStore } from '../../../hooks/storeContext';

const { Title, Text, Paragraph } = Typography;

const TIFFANY = '#2E8B8B';

// Public-access NIH author manuscript of the source study (served from /public).
const STUDY_PDF_URL = `${process.env.PUBLIC_URL || ''}/nihms569917.pdf`;

const usd = (n) => ((n || n === 0) ? `$${Number(n).toLocaleString()}` : '$—');

const currencyProps = {
  min: 0,
  step: 100,
  style: { width: '100%' },
  formatter: (v) => (v || v === 0 ? `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''),
  parser: (v) => (v ? v.replace(/\$\s?|(,*)/g, '') : ''),
};

const Field = ({ label, name, children, help, info }) => (
  <Form.Item
    label={
      <span style={{ fontSize: 13 }}>
        {label}
        {info && (
          <Tooltip title={info}>
            <InfoCircleOutlined style={{ marginLeft: 6, color: '#8c8c8c', cursor: 'help' }} />
          </Tooltip>
        )}
      </span>
    }
    name={name}
    style={{ marginBottom: 14 }}
    extra={help ? <Text type="secondary" style={{ fontSize: 11 }}>{help}</Text> : null}
  >
    {children}
  </Form.Item>
);

const CodeLine = ({ children }) => (
  <div
    style={{
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      background: '#10181f',
      color: '#d7f5f1',
      padding: '10px 12px',
      borderRadius: 8,
      fontSize: 12.5,
      lineHeight: 1.7,
      overflowX: 'auto',
      whiteSpace: 'pre-wrap',
      borderLeft: `3px solid ${TIFFANY}`,
    }}
  >
    {children}
  </div>
);

const FormulaBlock = ({ index, title, lines, note }) => (
  <div style={{ marginBottom: 18 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
      <span
        style={{
          width: 22, height: 22, borderRadius: '50%', background: '#1a1a1a', color: '#fff',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700,
        }}
      >
        {index}
      </span>
      <Text strong style={{ fontSize: 13 }}>{title}</Text>
    </div>
    <CodeLine>{lines}</CodeLine>
    {note && <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>{note}</Text>}
  </div>
);

const FormulasView = ({ vals }) => {
  const v = vals || {};
  const sys = v.baselineHighSystolic ?? 140;
  const dia = v.baselineHighDiastolic ?? 90;
  const minV = v.minVisitsForReturner ?? 2;
  const cap = v.rrrCap ?? 0.8;
  const miS = v.rrrPerMmHg?.mi?.systolic ?? 0.029;
  const miD = v.rrrPerMmHg?.mi?.diastolic ?? 0.0537;
  const stS = v.rrrPerMmHg?.stroke?.systolic ?? 0.0378;
  const stD = v.rrrPerMmHg?.stroke?.diastolic ?? 0.0787;
  const incMi = v.incidenceDefault?.mi ?? 11.4;
  const incSt = v.incidenceDefault?.stroke ?? 3.3;
  const cMi = v.costPerMI ?? 53200;
  const cSt = v.costPerStroke ?? 36000;
  const cEd = v.costPerEdVisit ?? 474;
  const pCost = v.defaultProgramCost;

  return (
    <div>
      <Paragraph type="secondary" style={{ fontSize: 12 }}>
        The numbers shown below are substituted live from your current settings, so this is exactly how the
        Community Impact page computes its results.
      </Paragraph>

      <FormulaBlock
        index={1}
        title="Eligible cohort"
        lines={`first-visit SBP \u2265 ${sys}  OR  DBP \u2265 ${dia}\nAND at least ${minV} blood-pressure visits`}
        note="Only members who arrived high and came back are counted."
      />
      <FormulaBlock
        index={2}
        title="Blood-pressure reduction"
        lines={'\u0394SBP = baseline SBP \u2212 average follow-up SBP\n\u0394DBP = baseline DBP \u2212 average follow-up DBP'}
      />
      <FormulaBlock
        index={3}
        title="Relative risk reduction (RRR)"
        lines={`RRR(MI)     = \u00bd \u00b7 [ min(${cap}, \u0394SBP\u00b7${miS}) + min(${cap}, \u0394DBP\u00b7${miD}) ]\nRRR(Stroke) = \u00bd \u00b7 [ min(${cap}, \u0394SBP\u00b7${stS}) + min(${cap}, \u0394DBP\u00b7${stD}) ]`}
        note={`Each term is capped at ${cap} (= ${Math.round(cap * 100)}%).`}
      />
      <FormulaBlock
        index={4}
        title="Events avoided"
        lines={`Expected(MI)     = ${incMi} / 1000 \u00b7 person-years\nExpected(Stroke) = ${incSt} / 1000 \u00b7 person-years\nAvoided          = Expected \u00b7 RRR`}
        note="Incidence is per 1,000 person-years (age-banded when ages are known)."
      />
      <FormulaBlock
        index={5}
        title="Dollars saved"
        lines={`BP savings = MI_avoided \u00b7 ${usd(cMi)} + Stroke_avoided \u00b7 ${usd(cSt)}\nER savings = (members who would have gone to the ER) \u00b7 ${usd(cEd)}\nTotal      = BP savings + ER savings`}
      />
      <FormulaBlock
        index={6}
        title="Return on investment"
        lines={`ROI = Total savings \u00f7 program operating cost\n      (${pCost ? usd(pCost) : 'entered on the page'})`}
      />
    </div>
  );
};

const ImpactSettingsDrawer = observer(({ open, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const { authStore } = useStore();
  const isAdmin = !!authStore?.isAdmin;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [citation, setCitation] = useState('');
  const [vals, setVals] = useState({});
  const [updatedByName, setUpdatedByName] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/screening/analytics/impact/settings');
      const payload = res?.data?.success !== undefined
        ? (res.data.success ? res.data.data : null)
        : (res?.data || res);
      if (payload?.settings) {
        form.setFieldsValue(payload.settings);
        setVals(payload.settings);
      }
      if (payload?.citation) setCitation(payload.citation);
      setUpdatedByName(payload?.updatedByName || null);
    } catch (err) {
      console.error('Error loading impact settings:', err);
      message.error('Failed to load calculation settings');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const values = form.getFieldsValue(true);
      const res = await api.put('/screening/analytics/impact/settings', values);
      const ok = res?.data?.success ?? res?.success ?? true;
      if (!ok) throw new Error('Save failed');
      message.success('Calculation settings saved');
      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error('Error saving impact settings:', err);
      message.error('Failed to save calculation settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      const res = await api.put('/screening/analytics/impact/settings', { reset: true });
      const payload = res?.data?.success !== undefined ? res.data.data : (res?.data || res);
      if (payload?.settings) {
        form.setFieldsValue(payload.settings);
        setVals(payload.settings);
      }
      message.success('Restored published defaults');
      onSaved?.();
    } catch (err) {
      console.error('Error resetting impact settings:', err);
      message.error('Failed to restore defaults');
    } finally {
      setSaving(false);
    }
  };

  const inputsTab = (
    <>
      {!isAdmin && (
        <Alert
          type="info"
          showIcon
          icon={<LockOutlined />}
          style={{ marginBottom: 16, borderRadius: 8 }}
          message="View only"
          description="Only administrators can change these values. You can review the inputs and formulas."
        />
      )}
      <Title level={5} style={{ marginBottom: 12 }}>Operating cost & cost assumptions (USD)</Title>
      <Field
        label="Program operating cost"
        name="defaultProgramCost"
        info="Your organization's total cost to run the program for the period you're analyzing — staff, vehicle, supplies, and admin. The return-on-investment is total savings divided by this number. The default shown is the figure from the research study; replace it with your own."
        help="Persisted. Drives ROI for everyone. Leave blank to hide the ROI ratio until a cost is set."
      >
        <InputNumber {...currencyProps} />
      </Field>
      <Field
        label="Cost per heart attack"
        name="costPerMI"
        info="The average amount the healthcare system spends caring for one heart attack over about 30 months. We multiply this by the number of heart attacks the program likely prevented to estimate dollars saved."
      >
        <InputNumber {...currencyProps} />
      </Field>
      <Field
        label="Cost per stroke"
        name="costPerStroke"
        info="The average amount the healthcare system spends caring for one stroke over about 30 months. We multiply this by the number of strokes the program likely prevented to estimate dollars saved."
      >
        <InputNumber {...currencyProps} />
      </Field>
      <Field
        label="Cost per avoided ER visit"
        name="costPerEdVisit"
        info="What one avoidable emergency-room visit typically costs. Each member who tells us they would have gone to the ER instead of the screening counts as one avoided visit."
      >
        <InputNumber {...currencyProps} />
      </Field>

      <Divider />
      <Title level={5} style={{ marginBottom: 12 }}>Who counts in the analysis</Title>
      <Row gutter={12}>
        <Col span={12}>
          <Field
            label="High systolic threshold"
            name="baselineHighSystolic"
            help="mmHg"
            info="The top number in a blood-pressure reading. A member is treated as 'high at their first visit' if their first reading is at or above this value. 140 is the standard cutoff."
          >
            <InputNumber min={80} max={220} style={{ width: '100%' }} />
          </Field>
        </Col>
        <Col span={12}>
          <Field
            label="High diastolic threshold"
            name="baselineHighDiastolic"
            help="mmHg"
            info="The bottom number in a blood-pressure reading. A member counts as high if their first reading is at or above this value. 90 is the standard cutoff."
          >
            <InputNumber min={40} max={140} style={{ width: '100%' }} />
          </Field>
        </Col>
      </Row>
      <Field
        label="Minimum visits to count as a returner"
        name="minVisitsForReturner"
        info="How many blood-pressure visits a member needs before we measure their improvement. Two means a first visit plus at least one follow-up, so we can compare before and after."
      >
        <InputNumber min={2} max={20} style={{ width: '100%' }} />
      </Field>

      <Divider />
      <Collapse
        ghost
        items={[
          {
            key: 'advanced',
            label: <Text strong>Advanced clinical coefficients</Text>,
            children: (
              <>
                <Paragraph type="secondary" style={{ fontSize: 12 }}>
                  These convert blood-pressure reductions into risk reductions and expected event rates.
                  Defaults reproduce the source paper — only change them with clinical guidance.
                </Paragraph>
                <Text strong style={{ fontSize: 13 }}>Risk reduction per mmHg</Text>
                <Row gutter={12} style={{ marginTop: 8 }}>
                  <Col span={12}>
                    <Field label="Heart attack · systolic" name={['rrrPerMmHg', 'mi', 'systolic']} info="How much heart-attack risk drops for each 1-point drop in the top blood-pressure number, from medical research. Leave as-is unless a clinician advises otherwise.">
                      <InputNumber min={0} max={0.2} step={0.001} style={{ width: '100%' }} />
                    </Field>
                  </Col>
                  <Col span={12}>
                    <Field label="Heart attack · diastolic" name={['rrrPerMmHg', 'mi', 'diastolic']} info="How much heart-attack risk drops for each 1-point drop in the bottom blood-pressure number, from medical research.">
                      <InputNumber min={0} max={0.2} step={0.001} style={{ width: '100%' }} />
                    </Field>
                  </Col>
                  <Col span={12}>
                    <Field label="Stroke · systolic" name={['rrrPerMmHg', 'stroke', 'systolic']} info="How much stroke risk drops for each 1-point drop in the top blood-pressure number, from medical research.">
                      <InputNumber min={0} max={0.2} step={0.001} style={{ width: '100%' }} />
                    </Field>
                  </Col>
                  <Col span={12}>
                    <Field label="Stroke · diastolic" name={['rrrPerMmHg', 'stroke', 'diastolic']} info="How much stroke risk drops for each 1-point drop in the bottom blood-pressure number, from medical research.">
                      <InputNumber min={0} max={0.2} step={0.001} style={{ width: '100%' }} />
                    </Field>
                  </Col>
                </Row>
                <Field label="Maximum risk reduction cap" name="rrrCap" help="0–1 (e.g. 0.80 = 80%)" info="A safety limit so a very large blood-pressure drop can't imply removing more than this share of risk. 0.80 means at most 80%.">
                  <InputNumber min={0} max={1} step={0.05} style={{ width: '100%' }} />
                </Field>

                <Text strong style={{ fontSize: 13 }}>Background incidence (per 1,000 person-years)</Text>
                <Row gutter={12} style={{ marginTop: 8 }}>
                  <Col span={12}>
                    <Field label="Heart attacks" name={['incidenceDefault', 'mi']} info="How many heart attacks normally happen each year per 1,000 people in this age group, with no program. It's the baseline we compare the program's results against.">
                      <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
                    </Field>
                  </Col>
                  <Col span={12}>
                    <Field label="Strokes" name={['incidenceDefault', 'stroke']} info="How many strokes normally happen each year per 1,000 people in this age group, with no program.">
                      <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
                    </Field>
                  </Col>
                </Row>
                <Field label="Fallback follow-up window (years)" name="fallbackFollowupYears" info="If we can't tell how long a member was followed between visits, we assume this many years when estimating their time in the program.">
                  <InputNumber min={0.25} max={10} step={0.25} style={{ width: '100%' }} />
                </Field>
              </>
            ),
          },
        ]}
      />
    </>
  );

  return (
    <Drawer
      title={
        <span>
          Cost & calculation settings
          <Popover
            placement="bottomLeft"
            title="About this model"
            content={
              <div style={{ maxWidth: 320, fontSize: 13, lineHeight: 1.6 }}>
                These numbers are adapted from a peer-reviewed study of an urban mobile health clinic
                (Song et al., <em>Health Affairs</em>, 2013) covering 5,900 patients across 10,509 visits.
                Members who arrived with high blood pressure and returned saw average drops of about
                10.7 / 6.2 mmHg, linked to roughly 32% and 45% lower relative risk of heart attack and
                stroke, and a conservative program return-on-investment of 1.3.
                <br /><br />
                Adjust the values here to match Black Hearts&rsquo; own costs and population — every number is
                an assumption you can change.
                <br /><br />
                <a href={STUDY_PDF_URL} target="_blank" rel="noopener noreferrer" style={{ color: TIFFANY, fontWeight: 600 }}>
                  <FilePdfOutlined /> Read the study (PDF)
                </a>
              </div>
            }
          >
            <InfoCircleOutlined style={{ marginLeft: 8, color: '#8c8c8c', cursor: 'help', fontSize: 14 }} />
          </Popover>
        </span>
      }
      width={480}
      open={open}
      onClose={onClose}
      destroyOnClose
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Popconfirm
            title="Restore published defaults?"
            description="This clears your custom values and reverts to the source-paper defaults."
            okText="Restore"
            cancelText="Cancel"
            onConfirm={handleReset}
            disabled={!isAdmin}
          >
            <Button icon={<ReloadOutlined />} disabled={!isAdmin || saving || loading}>Restore defaults</Button>
          </Popconfirm>
          <Space>
            <Button onClick={onClose}>Close</Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              onClick={handleSave}
              disabled={!isAdmin}
              style={isAdmin ? { background: TIFFANY, borderColor: TIFFANY } : undefined}
            >
              Save
            </Button>
          </Space>
        </div>
      }
    >
      <Form
        form={form}
        layout="vertical"
        disabled={loading || !isAdmin}
        onValuesChange={(_, all) => setVals(all)}
      >
        <Tabs
          defaultActiveKey="inputs"
          items={[
            { key: 'inputs', label: 'Operating cost & inputs', children: inputsTab },
            { key: 'formulas', label: 'Formulas', children: <FormulasView vals={vals} /> },
          ]}
        />
      </Form>

      {(citation || updatedByName) && (
        <>
          <Divider style={{ margin: '8px 0 12px' }} />
          {updatedByName && (
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
              Last edited by {updatedByName}
            </Text>
          )}
          {citation && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              Methodology source: {citation}{' '}
              <a href={STUDY_PDF_URL} target="_blank" rel="noopener noreferrer" style={{ color: TIFFANY, fontWeight: 600, whiteSpace: 'nowrap' }}>
                <FilePdfOutlined /> View PDF
              </a>
            </Text>
          )}
        </>
      )}
    </Drawer>
  );
});

export default ImpactSettingsDrawer;
